import {getConfig} from "../lib/configMgr";
import {RequestBuilder, toJSON} from "./request";
import {SearchCanceled, SearchError, SearchTimeoutError} from "./errors";
import {constructSearchAPIRequest} from "../lib/SearchContext";
import {getCustomLabels, wait} from "../lib/utils";
import {toast} from "react-toastify";

const { ExternalRESTAPI } = getConfig();

// Default retry configuration for derivative operations (graph, echains, connected, export)
// These operations depend on cached search results which may expire or be evicted
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 2000,
  maxDelayMs: 10000,
  backoffMultiplier: 1.5,
};

/**
 * Constructs the SSE stream URL from a stream_id
 * @param {string} streamId - The stream ID returned from the search API
 * @returns {string} The full stream URL
 */
export const getStreamURL = (streamId) => {
  if (!streamId) return null;
  return ExternalRESTAPI.Stream.indexOf("{streamId}") !== -1
    ? ExternalRESTAPI.Stream.replace("{streamId}", streamId)
    : `${ExternalRESTAPI.Stream}?channel=${streamId}`;
};

/**
 * Execute a fetch with retry logic for operations that depend on cached search results.
 * Retries on 202 (search still running) or cache miss scenarios.
 *
 * @param {Function} fetchFn - Async function that performs the fetch
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retry attempts
 * @param {number} options.initialDelayMs - Initial delay between retries
 * @param {number} options.maxDelayMs - Maximum delay between retries
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff
 * @param {Function} options.isCanceled - Function that returns true if operation should be canceled
 * @param {Function} options.onRetry - Callback called before each retry with (attempt, delay, reason)
 * @param {Object} options.searchParams - Original search params (required for cache miss recovery)
 * @param {Object} options.auth - Authentication data (required for cache miss recovery)
 * @param {Function} options.onCacheMissRecovery - Callback called when cache miss recovery is attempted
 * @returns {Promise} - Resolves with the fetch result or rejects after all retries exhausted
 */
const fetchWithRetry = async (fetchFn, options = {}) => {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.maxRetries,
    initialDelayMs = DEFAULT_RETRY_CONFIG.initialDelayMs,
    maxDelayMs = DEFAULT_RETRY_CONFIG.maxDelayMs,
    backoffMultiplier = DEFAULT_RETRY_CONFIG.backoffMultiplier,
    isCanceled = () => false,
    onRetry = null,
    searchParams = null,
    auth = null,
    onCacheMissRecovery = null,
  } = options;

  let lastError = null;
  let delay = initialDelayMs;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (isCanceled()) {
      throw new SearchCanceled("Operation canceled");
    }

    try {
      const result = await fetchFn();

      // Check if we got a 202 (search still running) - need to retry
      if (result?.status === 202) {
        lastError = new Error(result.info || "Search still in progress");
        lastError.status = 202;

        if (attempt < maxRetries) {
          if (onRetry) {
            onRetry(attempt + 1, delay, "Search still in progress");
          }
          await wait(delay);
          delay = Math.min(delay * backoffMultiplier, maxDelayMs);
          continue;
        }
      }

      // Check for 404 cache miss - search results expired
      if (result?.status === 404 && result?.error?.includes("Search ID")) {
        lastError = new Error("Cache miss - search results expired");
        lastError.status = 404;

        // Only retry with cache miss recovery if we have the required params
        if (attempt < maxRetries && searchParams && auth) {
          if (onCacheMissRecovery) {
            onCacheMissRecovery(attempt + 1, "Re-running search due to cache miss");
          }
          if (onRetry) {
            onRetry(attempt + 1, delay, "Cache miss - re-running search");
          }

          try {
            console.warn("Cache miss detected, re-running main search to populate cache...");

            // Notify user that we're recovering from cache miss
            toast.info("Search results expired. Re-running search...", {autoClose: 3000});

            // Re-run main search to populate cache
            // Use synchronous mode to ensure cache is populated before retrying
            await fetchSearch(
              searchParams,
              auth,
              {searchType: "search", useStreaming: false, waitForCompletion: true}
            );

            console.info("Search cache repopulated successfully, retrying operation...");
            toast.success("Search complete. Retrying operation...", {autoClose: 2000});

            // Wait before retrying the derivative operation
            await wait(delay);
            delay = Math.min(delay * backoffMultiplier, maxDelayMs);
            continue;
          } catch (searchError) {
            console.error("Failed to re-run search on cache miss:", searchError);
            toast.error("Failed to recover from expired search results");
            throw new SearchError("Failed to recover from cache miss: " + (searchError.message || String(searchError)));
          }
        } else if (!searchParams || !auth) {
          // Can't recover - missing required params
          throw new SearchError("Search results expired and cannot be recovered. Please re-run your search.");
        }
      }

      // Check for error status
      if (result?.status && result.status >= 400) {
        if (result.status === 413) {
          throw new SearchTimeoutError();
        }
        throw new SearchError(result.error || `Request failed with status ${result.status}`);
      }

      return result;
    } catch (error) {
      lastError = error;

      // Don't retry on client-side cancellation or explicit errors
      if (error instanceof SearchCanceled || error instanceof SearchError || error instanceof SearchTimeoutError) {
        throw error;
      }

      // Retry on network errors or unexpected failures
      if (attempt < maxRetries) {
        if (onRetry) {
          onRetry(attempt + 1, delay, error.message || "Network error");
        }
        await wait(delay);
        delay = Math.min(delay * backoffMultiplier, maxDelayMs);
        continue;
      }
    }
  }

  // All retries exhausted
  throw lastError || new Error("Operation failed after retries");
};

/**
 * Filter search results by dataset sources.
 * Returns IDs of entities in the specified category that have evidence from the given datasets.
 *
 * Example: "Which disease results have evidence from ClinicalTrials.gov or PubMed?"
 *
 * @param {Object} searchParams - Original search parameters (for cache recovery)
 * @param {Array} datasetsFilter - Array of dataset keys to filter by (e.g., ['pubmed', 'clinicaltrials'])
 * @param {string} category - Category to filter (e.g., 'disease', 'compound')
 * @param {Object} auth - Authentication data
 * @param {Object} options - Retry and callback options
 * @returns {Promise<{ids: Array}>} - Promise resolving to filtered entity IDs
 *
 * Includes retry logic for cache miss scenarios.
 */
export const fetchSearchByDataset = (searchParams, datasetsFilter, category, auth, options = {}) => {
  const {
    isCanceled = () => false,
    onRetry = null,
    onCacheMissRecovery = null,
    ...retryConfig
  } = options;

  return fetchWithRetry(
    () => fetchSearch(
      {...searchParams, datasetsFilter, category},
      auth,
      {searchType: "connected", useStreaming: false, waitForCompletion: true}
    ),
    {
      isCanceled,
      onRetry,
      onCacheMissRecovery,
      searchParams,  // Pass original search params for cache miss recovery
      auth,          // Pass auth for cache miss recovery
      ...retryConfig
    }
  );
};

/**
 * Fetch network graph data for visualization.
 * Includes retry logic for cache miss scenarios.
 *
 * @param {Object} searchParams - Search parameters including query IDs
 * @param {string} targetCategory - Category to generate graph for (or "all")
 * @param {Object} auth - Authentication data
 * @param {Object} options - Options including retry config and callbacks
 */
export const fetchGraphData = (searchParams, targetCategory, auth, options = {}) => {
  const {
    isCanceled = () => false,
    onRetry = null,
    onProgress = null,
    onCacheMissRecovery = null,
    ...retryConfig
  } = options;

  return fetchWithRetry(
    () => fetchSearch(
      {...searchParams, targetCategory},
      auth,
      {
        searchType: "graph",
        useStreaming: false,
        waitForCompletion: true,
        onProgress,
      }
    ),
    {
      isCanceled,
      onRetry,
      onCacheMissRecovery,
      searchParams,  // Pass original search params for cache miss recovery
      auth,          // Pass auth for cache miss recovery
      ...retryConfig
    }
  );
};

/**
 * Fetch evidence chains between query and target.
 * Includes retry logic for cache miss scenarios.
 *
 * @param {Object} searchParams - Search parameters including query IDs
 * @param {string} targetID - Target ID to find evidence chains to
 * @param {Array} datasetsFilter - Optional dataset filter
 * @param {Object} auth - Authentication data
 * @param {Object} options - Options including retry config and callbacks
 */
export const fetchEChains = (searchParams, targetID, datasetsFilter, auth, options = {}) => {
  const {
    isCanceled = () => false,
    onRetry = null,
    onProgress = null,
    onCacheMissRecovery = null,
    ...retryConfig
  } = options;

  const params = {...searchParams, targetID};
  if (datasetsFilter?.length) {
    params.datasetsFilter = datasetsFilter;
  }

  return fetchWithRetry(
    () => fetchSearch(
      params,
      auth,
      {
        searchType: "echains",
        useStreaming: false,
        waitForCompletion: true,
        onProgress,
      }
    ),
    {
      isCanceled,
      onRetry,
      onCacheMissRecovery,
      searchParams,  // Pass original search params for cache miss recovery
      auth,          // Pass auth for cache miss recovery
      ...retryConfig
    }
  );
};

/**
 * POST to /search/activity to trigger on-demand compound target activity computation.
 * Streams newline-delimited JSON progress events; calls onProgress({phase, total, computed})
 * for each event and resolves with the final {status, computed, total} when done.
 */
export const fetchCompoundActivity = async (searchId, auth, onProgress = null) => {
  const url = new URL(ExternalRESTAPI.SearchActivity, window.location.origin);
  url.searchParams.set("search_id", searchId);
  const req = new RequestBuilder(url);
  const response = await req.withAuthorization(auth).withJSONContentType().asPOST("{}").fetch();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let lastEvent = null;

  while (true) {
    const {done, value} = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, {stream: true});
    const lines = buffer.split("\n");
    buffer = lines.pop(); // keep incomplete last line
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const event = JSON.parse(line);
        lastEvent = event;
        onProgress?.(event);
      } catch (e) {
        console.warn("fetchComputeActivity: failed to parse line", line, e);
      }
    }
  }
  // Flush any remaining buffer content
  if (buffer.trim()) {
    try {
      lastEvent = JSON.parse(buffer);
      onProgress?.(lastEvent);
    } catch (e) { /* ignore */ }
  }
  return lastEvent;
};

export const fetchAgentStatus = async (agentId, auth) => {
  const urlString = ExternalRESTAPI.AgentStatus.replace("{agentId}", `${agentId}`);
  const url = new URL(urlString, window.location.origin);
  const req = new RequestBuilder(url);
  return req.withAuthorization(auth).asGET()
    .fetch()
    .then(r => r.json())
    .catch(() => null);
};

export const cancelAgent = async (agentId, auth, interrupt = false) => {
  const urlString = ExternalRESTAPI.CancelAgent.replace("{agentId}", `${agentId}`);
  const url = new URL(urlString, window.location.origin);
  const searchParams = url.searchParams;
  if (interrupt) {
    searchParams.set("interrupt", `${interrupt}`);
  }
  const req = new RequestBuilder(url);
  return req.withAuthorization(auth).asPOST()
    .fetch()
    .then(() => true)
    .catch(error => false);
};

export const fetchSearch = (searchParams, auth, params = {}) => {
  const {
    searchType = "search",
    isCanceled = () => false,
    onProgress = () => {},
    // If false, return immediately with a searchID; otherwise return once search results are ready
    waitForCompletion = true,
    // If true, use streaming mode (returns immediately with stream_id)
    useStreaming = false,
    // Extra params appended to URL after constructSearchAPIRequest (bypasses IGNOREPARAMS)
    extraParams = null,
  } = params;

  // Enable streaming by setting stream=true in search params
  const effectiveSearchParams = useStreaming
    ? {...searchParams, stream: true}
    : searchParams;

  // If `wait` is true, only returns when initial search results become available
  // Otherwise returns as soon as the searchID is obtained
  const req = constructSearchAPIRequest(ExternalRESTAPI.Search, effectiveSearchParams);
  if (extraParams) {
    Object.entries(extraParams).forEach(([k, v]) => req.url.searchParams.set(k, v));
  }
  return req
    .withJSONContentType()
    .withAuthorization(auth)
    .fetch()
    .then(toJSON)
    .then((result) => {
      if (isCanceled()) {
        throw new SearchCanceled("Search canceled");
      }
      const {
        id: searchID = null,
        status = null,
        page_token: pageToken = null,
        results = null,
        stream_id: streamID = null,
      } = result;
      if (!waitForCompletion) {
        return {...(searchID ? {searchID} : {}), ...result};
      }
      if (status && status >= 400) {
        if (status === 413) {
          throw new SearchTimeoutError();
        }
        throw new SearchError(result.error);
      }
      // If streaming is enabled and we got a stream_id, return immediately
      if (streamID) {
        return result;
      }
      if (results || status === 200) {
        return {...(searchType === "search" ? {pageToken} : {}), ...result};
      }
      // Legacy polling mode: continue polling for results
      onProgress(result.progress, result.total, result.info || "");
      // Ensure we don't continually pass the nocache flag
      delete req.url.searchParams.delete('nocache');
      // Make sure we use the most recent auth available...
      return wait(5000).then(() => fetchSearch(searchParams, req.auth, {searchType, isCanceled, onProgress}));
    })
};

/**
 * Export search results in various formats.
 * Includes retry logic for cache miss scenarios.
 *
 * @param {Object} params - Search parameters
 * @param {Object} auth - Authentication data
 * @param {string} format - Export format (csv, xlsx, svg, etc.)
 * @param {string} baseURL - Base URL for the export endpoint
 * @param {Object} options - Options including retry config
 */
export const exportSearchResults = async (params, auth, format = "svg", baseURL = ExternalRESTAPI.Search, options = {}) => {
  const {
    isCanceled = () => false,
    onRetry = null,
    onCacheMissRecovery = null,
    ...retryConfig
  } = options;

  const exportParams = {
    exportFormat: format,
    labels: getCustomLabels(),
  };

  return fetchWithRetry(
    async () => {
      const req = constructSearchAPIRequest(
        baseURL,
        {
          ...params,
          ...exportParams,
        },
        false
      );

      const response = await req
        .withJSONContentType()
        .withAuthorization(auth)
        .fetch();

      // For exports, we need to check if response is ok before returning
      // Export returns blob/file data directly, not JSON
      if (!response.ok) {
        // Try to parse error from response
        try {
          const errorData = await response.json();
          if (errorData.status === 202) {
            return errorData; // Return 202 for retry logic
          }
          if (errorData.status === 404 && errorData.error?.includes("Search ID")) {
            return errorData; // Return 404 for cache miss recovery
          }
          throw new SearchError(errorData.error || `Export failed with status ${response.status}`);
        } catch (e) {
          if (e instanceof SearchError) throw e;
          throw new SearchError(`Export failed with status ${response.status}`);
        }
      }

      return response;
    },
    {
      isCanceled,
      onRetry,
      onCacheMissRecovery,
      searchParams: params,  // Pass search params for cache miss recovery
      auth,                  // Pass auth for cache miss recovery
      ...retryConfig
    }
  );
};

/**
 * Check which molecules match a substructure query.
 * @param {string} query - SMARTS or SMILES substructure pattern
 * @param {string[]} molecules - Array of SMILES strings to test
 * @param {object} auth - Auth object with token
 * @returns {Promise<number[]>} Indices of matching molecules
 */
export const fetchSubstructureMatches = (query, molecules, auth) => {
  return new RequestBuilder(ExternalRESTAPI.MoleculeMatch + "/batch")
    .method("POST")
    .auth(auth)
    .body({ query, molecules })
    .fetch()
    .then(toJSON)
    .then(data => data.matches || []);
};